/**
 * The filters window: search, which categories and link kinds are shown, the shape legend, the
 * labels, groups and bundles switches and the Spacing slider (how far apart nodes are laid out).
 * Ported from the prototype's left rail. Nodes are added from the graph's right-click menu
 * (the Add node window), not here.
 */
import { For, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { bestMatch } from "../../shared/analysis";
import { SPACING_RANGE } from "../../state/scene";
import { linkKindShown, type LinkKindView } from "../../shared/kinds";
import { toast } from "../../shared/toast";
import { KindShape } from "../../shared/ui";
import { CategoryFilters } from "./CategoryFilters";

/** The swatch colour class of each link tone (as the nodal view colours them). */
const TONE_CLASS = { hierarchy: "sw-in", rule: "sw-dep", plain: "sw-use" } as const;

/** A small picture of a link kind's style (its colour, line and tip), for the link-kind toggles. */
function LinkSwatch(props: { kind: LinkKindView }): JSX.Element {
  const line = () => TONE_CLASS[props.kind.tone];
  const triangle = () => props.kind.head === "hollow" || props.kind.head === "filled";
  const dashes = () => (props.kind.line === "dashed" ? "4 3" : props.kind.line === "dotted" ? "1.5 2.5" : undefined);
  return (
    <svg viewBox="0 0 30 12" aria-hidden="true">
      <path class={line()} d={`M0 6H${triangle() ? 21 : 30}`} stroke-dasharray={dashes()} />
      {triangle() ? <path class="sw-tri" d="M30 6L21 1.5V10.5Z" /> : <path class={line()} d="M24 2.5L30 6L24 9.5" />}
    </svg>
  );
}

/** The filters window. */
export function FiltersWindow(): JSX.Element {
  const { view, graph, scene, kinds } = useApp();
  /** The whole graph. */
  const g = () => graph.state.graph;
  /** How many links have a kind. */
  const linkCount = (k: string) => g().links.filter((l) => l.kind === k).length;
  /** Every link kind to filter: the defined ones, then those the graph uses that nobody defines. */
  const linkKinds = () => {
    const defined = kinds.links();
    const others = [...new Set(g().links.map((l) => l.kind))].filter((k) => !defined.some((d) => d.id === k));
    return [...defined, ...others.map((kind) => kinds.ofLink({ kind }))];
  };

  return (
    <div class="rail">
      <div>
        <label class="sr" for="search">
          Find a node
        </label>
        <input
          class="search"
          id="search"
          type="search"
          placeholder="Find a class, library or language"
          autocomplete="off"
          onInput={(e) => view.setQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || !view.state.query) return;
            const visible = g().nodes.filter((n) => scene.byId.get(n.id)?.visible ?? true);
            const found = bestMatch(visible, view.state.query);
            if (found) {
              view.selectNode(found.id);
              view.requestCenter(found.id);
            } else toast(`No node matches "${e.currentTarget.value.trim()}".`);
          }}
        />
      </div>
      <CategoryFilters />
      <div>
        <h2>Links</h2>
        <div class="stack">
          <For each={linkKinds()}>
            {(k) => (
              <button type="button" class="chip" data-link-filter={k.id} aria-pressed={linkKindShown(view.state.linkKinds, k.id)} onClick={() => view.toggleLinkKind(k.id)}>
                <LinkSwatch kind={k} />
                {k.label}
                <span class="n">{linkCount(k.id)}</span>
              </button>
            )}
          </For>
        </div>
      </div>
      <div>
        <h2>Shapes</h2>
        <div class="legend">
          <For each={kinds.nodes()}>
            {(k) => (
              <div>
                <KindShape shape={k.shape} />
                {k.label}
              </div>
            )}
          </For>
        </div>
      </div>
      <label class="check">
        <input type="checkbox" checked={view.state.labels} onChange={(e) => view.setLabels(e.currentTarget.checked)} /> Show labels
      </label>
      <label class="check">
        <input id="show-groups" type="checkbox" checked={view.state.groups} onChange={(e) => view.setGroups(e.currentTarget.checked)} /> Show groups
      </label>
      <label class="check" classList={{ muted: !view.state.groups }}>
        <input id="bundle-links" type="checkbox" checked={view.state.bundles} disabled={!view.state.groups} onChange={(e) => view.setBundles(e.currentTarget.checked)} /> Bundle links
        between groups
      </label>
      <label class="slider" title="How far apart nodes are laid out (big projects read better around 2)">
        <span>Spacing</span>
        <input
          id="graph-spacing"
          type="range"
          min={SPACING_RANGE.min}
          max={SPACING_RANGE.max}
          step="0.1"
          value={view.state.spacing}
          onInput={(e) => view.setSpacing(Number(e.currentTarget.value))}
        />
        <output id="graph-spacing-value">{view.state.spacing.toFixed(1)}×</output>
      </label>
    </div>
  );
}
